import { useSignals } from '@preact/signals-react/runtime';
import { Avatar, Row } from 'antd';
import { ColumnsType } from 'antd/es/table';
import Box from 'components/Box';
import Button from 'components/Button';
import Table from 'components/Table';
import Text from 'components/Text';
import colorTheme from 'constants/color';
import appointmentActions from 'features/appointment/services/actions';
import {
  APPOINTMENT_SOURCE_TYPE,
  APPOINTMENT_STATUS,
  appointmentStatusObject,
  CALENDAR_ITEM_TYPES,
  getAptStatusTheme,
} from 'features/appointment/services/constants';
import appointmentSelectors from 'features/appointment/services/selectors';
import { IAppointmentItemData } from 'features/appointment/services/types/appointment';
import { FORMAT_TIME } from 'features/appointment/utils/format';
import { TableGreenEvenStyled } from 'features/report/components/Component';
import { get, isNil } from 'lodash';
import React, { useEffect } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { maskPhone } from 'utils/formatPhone';
import { momentTimezone } from 'utils/time';
import { aptDetailV3Ref } from '../Calendar/TableView/Body/Modal/AppointmentDetailV3';
import { appointmentDetailRef } from '../Calendar/TableView/Body/Modal/AppointmentDetail';
import { ICalendarItemData } from 'features/appointment/services/types/calendar';
type IListLayoutProps = {
  v2?: boolean;
};
const ListLayout: React.FC<IListLayoutProps> = ({ v2 }) => {
  useSignals();
  const dispatch = useAppDispatch();
  const loading = appointmentSelectors.table.loading();
  const data = appointmentSelectors.table.appointments();
  const params = appointmentSelectors.table.params();
  const totalElements = appointmentSelectors.table.totalElements();

  useEffect(() => {
    dispatch(appointmentActions.setTableParams({ staffId: params.staffId, page: 1, status: params.status }));
    return () => {
      dispatch(appointmentActions.clearTableParams());
    };
  }, []);

  const handlePageChange = async (page?: number) => {
    const params = {
      page: page,
    };
    dispatch(appointmentActions.setTableParams(params));
  };
  const columns: ColumnsType<IAppointmentItemData> = [
    {
      title: 'Staff',
      align: 'left',
      render(value, data) {
        if (!data?.staffName) return <Text>{'--'}</Text>;
        return (
          <Row align={'middle'} style={{ gap: '4px' }}>
            <Avatar size={v2 ? 60 : 'small'} src={data?.avatar}>
              {get(data, ['staffName', 0], '')}
            </Avatar>
            <Text>{data?.staffName || ''}</Text>
          </Row>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'startDate',
      align: 'left',
      render(value, record) {
        return (
          <>
            <Text variant='BODY_1'>{momentTimezone(record.startTime).format('MM-DD-YYYY')}</Text>
          </>
        );
      },
    },
    {
      title: 'Time',
      dataIndex: 'startDate',
      align: 'left',
      render(value, record) {
        return (
          <Text variant='BODY_1'>
            {momentTimezone(record.startTime).format(FORMAT_TIME)} -{' '}
            {momentTimezone(record.endTime).format(FORMAT_TIME)}
          </Text>
        );
      },
    },
    {
      title: 'Customer',
      align: 'left',
      render(value, record) {
        return <Text>{record?.customerName || '--'}</Text>;
      },
    },
    {
      title: 'Customer Phone',
      align: 'left',
      render(value, record) {
        return <Text>{maskPhone(record?.customerPhone || '') || '--'}</Text>;
      },
    },

    {
      title: 'Services',
      align: 'left',
      render(value, record) {
        const services = record?.services || [];
        return (
          <>
            {services?.length ? (
              <>
                {services.map((s) => (
                  <Text key={s.serviceId}>
                    {s.serviceName}({s.duration}m)
                  </Text>
                ))}
              </>
            ) : (
              '--'
            )}
          </>
        );
      },
    },
    {
      title: 'Source Type',
      align: 'left',
      render(value, data) {
        if (!data?.sourceType) return <Text>{'--'}</Text>;

        switch (data.sourceType) {
          case APPOINTMENT_SOURCE_TYPE.ONLINE:
            return (
              <Row align={'middle'} style={{ gap: '4px' }}>
                <EarthIcon />
                <Text variant='BODY_1'>Website</Text>
              </Row>
            );
          case APPOINTMENT_SOURCE_TYPE.POS:
            return (
              <Row align={'middle'} style={{ gap: '4px' }}>
                <CallingIcon />
                <Text variant='BODY_1'>Call-in</Text>
              </Row>
            );
          case APPOINTMENT_SOURCE_TYPE.APP:
            return (
              <Row align={'middle'} style={{ gap: '4px' }}>
                <IconByApp />
                <Text variant='BODY_1'>App</Text>
              </Row>
            );

          default:
            return <Text>{'--'}</Text>;
        }
      },
    },
    {
      title: 'Note',
      dataIndex: 'note',
      align: 'left',
      render(_, record) {
        return <Text>{record?.note || '--'}</Text>;
      },
    },
    {
      title: 'Status',
      align: 'left',
      render(_, record) {
        if (isNil(record?.status)) return null;
        const label = get(appointmentStatusObject, [parseFloat(record?.status) as any], '') || '';
        return (
          <Box justifyContent='start' display='flex'>
            <BoxItem className={`BoxItem status_${record?.status}`} type={record?.status}>
              <span>{label}</span>
            </BoxItem>
          </Box>
        );
      },
    },
    {
      title: 'Actions',
      align: 'center',
      render(_, record) {
        const data: ICalendarItemData = {
          data: record,
          endTime: record.endTime,
          startTime: record.startTime,
          id: record.appointmentId,
          staffId: record.staffId,
          type: CALENDAR_ITEM_TYPES.APPOINTMENT,
        };

        return (
          <Box justifyContent='center' display='flex' alignItems='center'>
            <ActionButton
              color={colorTheme.fill_1}
              bgColor={colorTheme.info_3}
              v2={v2}
              onClick={() => {
                if (record.version === 'v3') return aptDetailV3Ref.current?.open(data.id);
                appointmentDetailRef.current?.open({
                  data: data,
                  headerData: null,
                });
              }}
            >
              DETAILS
            </ActionButton>
          </Box>
        );
      },
    },
  ];

  return (
    <ListLayoutStyled className={v2 ? 'v2' : ''}>
      <Table
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={'index'}
        pagination={{
          total: totalElements ?? 0,
          pageSize: params.size,
          current: params.page,
          showSizeChanger: false,
        }}
        handleChangePage={handlePageChange}
        emptyMsg='No appointment'
      />
    </ListLayoutStyled>
  );
};

export default ListLayout;
type ListLayoutStyledProps = {};
const ListLayoutStyled = styled(TableGreenEvenStyled)<ListLayoutStyledProps>`
  &.v2 {
    padding: 0 16px;
    padding-top: 8px;

    .BoxItem {
      border-radius: 5px;
      display: flex;
      padding: 4px 8px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      flex: unset;

      color: #fff;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;

      &.status_${APPOINTMENT_STATUS.NEW} {
        background: #6fabb6;
        color: #fff;
      }
      &.status_${APPOINTMENT_STATUS.CONFIRM} {
        background: #6fe7bf;
        color: #346751;
      }
      &.status_${APPOINTMENT_STATUS.NO_SHOW} {
        background: #e5e6eb;
        color: #1d2129;
      }
      &.status_${APPOINTMENT_STATUS.CANCEL} {
        background: #f5767f;
        color: #fff;
      }
    }

    .ant-table-cell {
      padding: 0.5rem !important;
    }
      
    th.ant-table-cell {
      border-right: 1px solid #fff;
      text-align: center !important;
      &:nth-last-child(1) {
        border-right: none;
      }
      padding: 14px 0.5rem !important;
    }
    .ant-table-wrapper .ant-table .ant-table-content {
      max-height: unset;
      overflow: unset;
      .ant-table-thead th.ant-table-cell,
      .ant-table-thead td.ant-table-cell {
        height: 48px;
        padding: 0px 8px;
        gap: 10px;
        align-self: stretch;
        border-bottom: 1px solid #ccd4dc;
        background: #6fabb6;
        color: #fff;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-transform: capitalize;
        text-align: left !important;
        position: sticky;
        top: 0;
        &::before {
          width: 1.5px;
          height: 100%;
        }
      }

      .ant-table-tbody td.ant-table-cell {
        .BODY_1,
        .CONTENT_2 {
          color: #1d2129;
          font-family: Poppins;
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }
      }

      .ant-table-tbody .ant-table-row:nth-child(even) td {
        background: #e4f4f6;
        border-bottom: 1px solid #e4f4f6;
      }
      .ant-table-tbody .ant-table-row:nth-child(odd) td {
        background: #fff;
      }

      .ant-table-selection-column {
        text-align: center !important;
        .ant-checkbox-wrapper {
          zoom: 1.8;
        }
      }
    }
  }
`;

const BoxItem = styled.div<{ type: APPOINTMENT_STATUS; multiple?: boolean }>`
  color: var(--text-text-3, #1d2129);
  text-align: center;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  display: flex;
  padding: 0.5rem 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;

  ${({ type }) => {
    const theme = getAptStatusTheme(type);
    return `
      border: 1.5px solid ${theme.fill} !important;
      background: ${theme.stroke} !important;
      `;
  }}
`;

const EarthIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
    <path
      d='M8.84812 0H9.11742C10.4741 0.0221484 11.8287 0.339961 13.0402 0.954844C15.0205 1.94836 16.612 3.69633 17.4027 5.76773C17.7827 6.74859 17.9761 7.7973 18 8.84812V9.11812C17.9771 10.6045 17.5936 12.0871 16.8627 13.3836C16.1849 14.5955 15.223 15.647 14.0773 16.4317C12.6299 17.434 10.8787 17.9757 9.12059 18H8.8541C7.23199 17.9705 5.61551 17.5078 4.24195 16.6405C3.20836 15.995 2.30766 15.1383 1.61121 14.1384C0.581836 12.6795 0.0246094 10.9041 0 9.12094V8.8548C0.0302344 7.07027 0.592383 5.29523 1.62738 3.83836C2.48414 2.61633 3.64781 1.6098 4.98375 0.942891C6.17871 0.340664 7.51184 0.028125 8.84812 0ZM7.24992 1.90582C6.53062 2.64586 6.08062 3.59684 5.73187 4.55625C6.17695 4.59598 6.62133 4.63957 7.06605 4.67789C7.53434 4.70074 8.00297 4.71867 8.4716 4.73941C8.4716 3.53637 8.4716 2.33332 8.4716 1.13027C8.00191 1.2709 7.58988 1.55812 7.24992 1.90582ZM9.52734 1.12992C9.52734 2.33402 9.52734 3.53848 9.52734 4.74293C9.99457 4.725 10.4621 4.70988 10.9294 4.6902C11.3783 4.65504 11.8269 4.61637 12.2755 4.57875C12.0259 3.89039 11.7316 3.21152 11.3161 2.60578C10.8795 1.95926 10.2927 1.36055 9.52734 1.12992ZM2.82727 3.99902C3.1159 4.07145 3.40418 4.14668 3.69352 4.21699C4.01801 4.28379 4.34391 4.3432 4.6691 4.40613C4.90324 3.70195 5.19047 3.01113 5.58246 2.37867C5.73223 2.09918 5.93438 1.85309 6.11508 1.59363C4.83469 2.09285 3.69492 2.93449 2.82727 3.99902ZM11.8849 1.59363C12.0663 1.85414 12.2692 2.10129 12.4196 2.38219C12.8166 3.02344 13.1048 3.7248 13.3425 4.43848C13.6628 4.37941 13.9837 4.32387 14.304 4.26305C14.6071 4.19484 14.9087 4.11891 15.2111 4.04719C14.3395 2.95945 13.1843 2.10023 11.8849 1.59363ZM2.18777 4.90781C1.53984 5.9857 1.15699 7.21793 1.07367 8.47266C2.04715 8.47266 3.02098 8.47301 3.9948 8.47231C4.03066 7.4482 4.15441 6.42727 4.38293 5.42812C3.64184 5.2977 2.90602 5.13281 2.18777 4.90781ZM13.6245 5.46117C13.8473 6.44977 13.9704 7.45875 14.0052 8.47125C14.979 8.47195 15.9529 8.4716 16.9263 8.47125C16.8441 7.23902 16.4735 6.02789 15.8456 4.96371C15.1172 5.17852 14.3733 5.33707 13.6245 5.46117ZM5.05055 8.47125C6.19102 8.4716 7.33113 8.4716 8.4716 8.4716C8.4716 7.57934 8.47195 6.68742 8.47125 5.79551C7.45559 5.77195 6.44062 5.70621 5.43199 5.58246C5.20699 6.52957 5.08781 7.49918 5.05055 8.47125ZM9.52875 5.79867C9.52805 6.68988 9.5284 7.58074 9.5284 8.4716C10.6689 8.4716 11.809 8.4716 12.9495 8.47125C12.915 7.50727 12.793 6.5468 12.575 5.60672C11.5636 5.72062 10.5465 5.7825 9.52875 5.79867ZM1.07367 9.52734C1.15559 10.7441 1.51629 11.9405 2.13047 12.9945C2.86664 12.7733 3.62004 12.6148 4.37766 12.4868C4.15723 11.5151 4.03559 10.523 3.99691 9.5277C3.02238 9.52699 2.04785 9.52734 1.07367 9.52734ZM5.05266 9.52875C5.09238 10.4752 5.20945 11.4184 5.42812 12.3405C6.43887 12.2266 7.45488 12.1644 8.4716 12.1482C8.47406 11.275 8.47195 10.4017 8.47266 9.5284C7.33254 9.5284 6.19277 9.52805 5.05266 9.52875ZM9.5284 9.5284C9.52875 10.4027 9.52805 11.2767 9.52875 12.1511C10.5444 12.1714 11.5587 12.2404 12.567 12.3616C12.787 11.4325 12.9087 10.4822 12.9473 9.52875C11.8076 9.5284 10.6682 9.5284 9.5284 9.5284ZM14.0031 9.52875C13.9669 10.5346 13.8361 11.5355 13.6181 12.5177C14.3694 12.6457 15.114 12.8148 15.8414 13.0426C16.4725 11.9774 16.8437 10.7634 16.9263 9.52875C15.9521 9.5284 14.9776 9.52805 14.0031 9.52875ZM7.48758 13.2395C6.89906 13.2532 6.31441 13.3253 5.72766 13.3692C6.02648 14.1936 6.39563 15.0068 6.95004 15.6934C7.34484 16.1905 7.85426 16.6233 8.4716 16.8086C8.4716 15.6069 8.4716 14.4049 8.47125 13.2033C8.14324 13.2156 7.81559 13.2275 7.48758 13.2395ZM9.52875 13.2068C9.52805 14.4074 9.5284 15.608 9.5284 16.8082C10.2716 16.5853 10.8454 16.0119 11.2774 15.39C11.7063 14.7772 12.0108 14.0885 12.266 13.3882C11.822 13.3478 11.3787 13.3056 10.9346 13.2673C10.466 13.2448 9.99738 13.2272 9.52875 13.2068ZM3.65906 13.6948C3.35742 13.7651 3.05684 13.8396 2.7559 13.912C3.64852 15.0388 4.84242 15.9251 6.18785 16.4341C5.48613 15.577 5.00449 14.5607 4.66453 13.512C4.32844 13.5686 3.99375 13.6318 3.65906 13.6948ZM13.3302 13.5373C12.986 14.5758 12.5093 15.5841 11.8121 16.4341C13.1404 15.9311 14.3223 15.0616 15.21 13.9539C14.9164 13.8793 14.6236 13.8034 14.3297 13.7306C13.9971 13.6628 13.6631 13.6027 13.3302 13.5373Z'
      fill='#1D2129'
    />
  </svg>
);

const CallingIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
    <path
      d='M17.6777 7.6716C17.8949 7.51589 18.0219 7.24313 17.9969 6.97626C17.9045 6.15171 17.684 5.34152 17.3428 4.58508C16.5458 2.80973 15.0594 1.35536 13.2657 0.599652C12.5725 0.296705 11.8319 0.118544 11.0839 0.0162119L10.8992 0.0162119C10.5749 0.0515498 10.2837 0.30149 10.2182 0.624314C10.142 0.930941 10.28 1.27217 10.5428 1.44518C10.7556 1.5972 11.0287 1.58874 11.2757 1.63475C12.4713 1.84494 13.6051 2.40666 14.4878 3.24077C15.5531 4.22949 16.2506 5.60398 16.4218 7.0473C16.4457 7.31528 16.5804 7.58178 16.8215 7.71503C17.0818 7.87221 17.4355 7.85565 17.6777 7.6716Z'
      fill='#1D2129'
    />
    <path
      d='M16.875 16.846C17.1003 16.4805 17.214 16.0527 17.2169 15.6246C17.218 14.8513 17.218 14.0775 17.2169 13.3038C17.2147 12.9276 17.1371 12.5499 16.9659 12.2135C16.639 11.5398 15.9687 11.0528 15.2307 10.9354C14.6016 10.8438 13.9766 10.7116 13.3766 10.4981C13.1583 10.4249 12.9411 10.3417 12.7125 10.3056C12.2469 10.2257 11.758 10.292 11.3303 10.4926C10.8319 10.7094 10.4917 11.1493 10.1038 11.5108C8.65456 10.6001 7.42179 9.35442 6.49896 7.91405C6.80633 7.59306 7.15897 7.30631 7.39198 6.92238C7.76817 6.33158 7.84106 5.5674 7.60879 4.90887C7.40707 4.33353 7.22854 3.74715 7.13688 3.14309C7.08719 2.80923 7.03639 2.46763 6.8862 2.16137C6.52915 1.36885 5.70019 0.824428 4.83221 0.800871C4.0095 0.796821 3.18643 0.800871 2.36372 0.798662C1.71292 0.79314 1.0658 1.07547 0.626288 1.55548C0.268126 1.93646 0.0491068 2.44149 0.00824755 2.96199C-0.0164145 3.19684 0.0196592 3.43205 0.0513155 3.66432C0.458435 7.0214 1.97096 10.2173 4.24177 12.7148C5.86215 14.4957 7.89554 15.8831 10.1089 16.8202C11.3229 17.3146 12.5914 17.6772 13.8886 17.8715C14.2799 17.9161 14.6704 18.0158 15.0669 17.9746C15.8034 17.9146 16.4969 17.481 16.875 16.846ZM15.6378 15.7619C15.5833 16.1223 15.245 16.4142 14.8806 16.4161C11.4532 16.0851 8.17971 14.4868 5.76387 12.0405C3.53539 9.75933 2.03722 6.75895 1.62274 3.59181C1.59255 3.3496 1.51746 3.09082 1.62384 2.85634C1.73758 2.56039 2.04936 2.3572 2.36556 2.36824C3.16324 2.36677 3.96054 2.36935 4.75822 2.36677C5.06668 2.3756 5.37294 2.57033 5.47969 2.86555C5.55368 3.075 5.5662 3.29954 5.60706 3.51635C5.69025 3.99746 5.81246 4.47083 5.96043 4.93574C6.02338 5.12642 6.09553 5.31452 6.14853 5.50814C6.22215 5.77649 6.12608 6.07649 5.92546 6.26606C5.6019 6.5922 5.27429 6.91428 4.95221 7.24189C4.72214 7.47159 4.66104 7.84595 4.80312 8.13748C5.66006 9.64522 6.80559 10.991 8.17051 12.0622C8.44143 12.2878 8.73812 12.4792 9.02192 12.6879C9.26598 12.8517 9.52254 12.996 9.77101 13.1532C9.99187 13.2997 10.2863 13.3317 10.5297 13.225C10.6865 13.1606 10.8039 13.0343 10.9206 12.9165C11.2033 12.6349 11.4805 12.3482 11.7646 12.0681C11.9189 11.9241 12.1283 11.8317 12.3415 11.845C12.5123 11.8457 12.6691 11.9208 12.8292 11.9694C13.4701 12.1932 14.1319 12.3603 14.8044 12.4549C14.9782 12.4851 15.1618 12.5127 15.3069 12.6209C15.5277 12.7704 15.6536 13.038 15.6444 13.3023C15.6396 14.1221 15.6529 14.9426 15.6378 15.7619Z'
      fill='#1D2129'
    />
    <path
      d='M14.8359 7.22387C14.8653 7.1046 14.8664 6.97982 14.8396 6.86018C14.6529 5.94325 14.1913 5.08263 13.5203 4.42925C12.8581 3.77329 11.9934 3.32826 11.0768 3.16114C10.8711 3.13611 10.6528 3.18949 10.4905 3.32053C10.2898 3.4766 10.1735 3.73464 10.1927 3.98826C10.2089 4.2706 10.3907 4.53784 10.6506 4.65048C10.8262 4.72704 11.0205 4.74029 11.1994 4.80692C12.2264 5.13232 13.0513 6.02828 13.2777 7.08325C13.3175 7.28644 13.3966 7.49258 13.5597 7.62878C13.7743 7.81725 14.096 7.87688 14.3614 7.76829C14.5966 7.67921 14.7792 7.46829 14.8359 7.22387Z'
      fill='#1D2129'
    />
  </svg>
);

const IconByApp = () => (
  <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect width='24' height='24' rx='5' fill='#D2464F' />
    <rect x='4.69751' y='4.69751' width='6.32028' height='6.32028' rx='0.683274' stroke='white' strokeWidth='1.02491' />
    <rect x='4.69751' y='12.9822' width='6.32028' height='6.32028' rx='0.683274' stroke='white' strokeWidth='1.02491' />
    <rect x='12.9827' y='4.69751' width='6.32028' height='6.32028' rx='0.683274' stroke='white' strokeWidth='1.02491' />
    <rect x='12.9827' y='12.9822' width='6.32028' height='6.32028' rx='0.683274' stroke='white' strokeWidth='1.02491' />
  </svg>
);

const ActionButton = styled(Button).withConfig({
  shouldForwardProp: (prop) => !['bgColor'].includes(prop),
})<{ bgColor?: string; color?: string; v2?: boolean }>`
  display: flex;
  padding: 8px 4px;
  justify-content: center;
  align-items: center;
  border-radius: 5px;
  background: ${(props) => props.bgColor || 'var(--info-infor-3, #6fabb6)'} !important;
  cursor: pointer;
  color: ${(props) => props.color || 'var(--text-text-1, #fff)'} !important;
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  border-color: ${(props) => props.bgColor || 'var(--info-infor-3, #6fabb6)'} !important;
  &:hover {
    opacity: 0.8;
  }
  &:disabled {
    opacity: 0.5;
  }

  display: flex;
  height: 3rem !important;
  padding: 8px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  background: #6fabb6 !important;
  box-shadow:
    0px 2px 4px 0px rgba(44, 35, 66, 0.4),
    0px 4px 8px -3px rgba(45, 35, 66, 0.3),
    0px -4px 0px 0px #144b55 inset !important;
  .CONTENT_2,
  span {
    color: #fff !important;
    text-align: center;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }
  ${({ bgColor }) => `
    ${
      bgColor === colorTheme.error_2 &&
      `
      border-radius: 4px;
      background: #FF8890 !important;
      box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -4px 0px 0px #E7646D inset !important;
    `
    }
    ${
      bgColor === colorTheme.fill_2 &&
      `
      border-radius: 4px;
      background: #E5E6EB !important;
      box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #828295 inset !important;
      border: none;
      .CONTENT_2, span {
        color: #1D2129 !important;
      }
    `
    }
    ${
      bgColor === colorTheme.info_3 &&
      `
      border-radius: 4px;
      border: 1.5px solid #2D6772  !important;
      background: #FFF !important;
      box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #144B55 inset !important;

      .CONTENT_2, span {
        color: #2D6772 !important;
      }
    `
    }
 `}
`;
